import { useMutation } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import useCanCommentRun from "shared/Run/access/useCanCommentRun";
import { Stack, Module } from "types/generated";
import Tooltip from "ds/components/Tooltip";

import { ADD_RUN_COMMENT } from "./gql";

type CommentFormProps = {
  stack: Stack | Module;
  runId: string;
  runQueryToRefetch: string;
};

const CommentForm = ({ stack, runId, runQueryToRefetch }: CommentFormProps) => {
  const [comment, setComment] = useState("");

  const { reportSuccess, onError } = useTypedContext(FlashContext);

  const canComment = useCanCommentRun()(stack);

  const [addRunComment, { loading: isCommentSending }] = useMutation(ADD_RUN_COMMENT, {
    refetchQueries: [runQueryToRefetch],
    variables: { stackId: stack.id, runId, body: comment },
    awaitRefetchQueries: true,
  });

  const handleSendComment = useCallback(async () => {
    try {
      await addRunComment();

      setComment("");
      reportSuccess({ message: "Comment successfully added" });
    } catch (error) {
      onError(error);
    }
  }, [addRunComment, onError, reportSuccess]);

  const buttonDisabled = useMemo(() => !comment.trim(), [comment]);

  return (
    <Box gap="medium" align="start" fullWidth>
      <Textarea
        minRows={1}
        maxRows={10}
        placeholder="Share your thoughts..."
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        disabled={isCommentSending || !canComment.result}
      />
      <Tooltip
        textAlign="center"
        active={!!canComment.message}
        on={(tooltipProps) => (
          <Button
            {...tooltipProps}
            variant="secondary"
            disabled={buttonDisabled || !canComment.result}
            onPress={handleSendComment}
            loading={isCommentSending}
          >
            Comment
          </Button>
        )}
      >
        {canComment.message}
      </Tooltip>
    </Box>
  );
};

export default CommentForm;
